import { Literal, ValidatorKit } from './types'
import { ValidationResult, Validator } from '../public'
import { devWarn } from '../util/devWarn'

/**
 * Base (true/false) runtime validators for common types and formats.
 * These validators are available through the `v` namespace.
 */
export const baseValidators: ValidatorKit = {
  /**
   * Validates that a value is a string.
   * @param v - The value to validate
   * @param field - Optional field name for the validation result
   * @returns An array containing a single validation result
   */
  isString: (v: unknown, field?: string): ValidationResult[] => {
    const valid = typeof v === 'string'
    return [{ field: field || '', value: v,valid, message: valid ? undefined : `Expected string, got ${typeof v}` }]
  },

  /**
   * Validates that a value is a number.
   * @param v - The value to validate
   * @param field - Optional field name for the validation result
   * @returns An array containing a single validation result
   */
  isNumber: (v: unknown, field?: string): ValidationResult[] => {
    const valid = typeof v === 'number'
    return [{ field: field || '', value: v, valid, message: valid ? undefined : `Expected number, got ${typeof v}` }]
  },

  /**
   * Validates that a value is an integer.
   * @param v - The value to validate
   * @param field - Optional field name for the validation result
   * @returns An array containing a single validation result
   */
  isInteger: (v: unknown, field?: string): ValidationResult[] => {
    const valid = typeof v === 'number' && Number.isInteger(v)
    return [{ field: field || '', value: v, valid, message: valid ? undefined : `Expected integer, got ${typeof v}` }]
  },

  /**
   * Validates that a value is a boolean.
   * @param v - The value to validate
   * @param field - Optional field name for the validation result
   * @returns An array containing a single validation result
   */
  isBoolean: (v: unknown, field?: string): ValidationResult[] => {
    const valid = typeof v === 'boolean'
    return [{ field: field || '', value: v, valid, message: valid ? undefined : `Expected boolean, got ${typeof v}` }]
  },

  /**
   * Validates that a value is a string that can be converted to a number.
   * @param v - The value to validate
   * @param field - Optional field name for the validation result
   * @returns An array containing a single validation result
   */
  isNumericString: (v: unknown, field?: string): ValidationResult[] => {
    const valid = typeof v === 'string' && !isNaN(Number(v))
    return [{ field: field || '', value: v, valid, message: valid ? undefined : `Invalid numeric string: ${v}` }]
  },

  /**
   * Validates that a value is a string that represents an integer.
   * @param v - The value to validate
   * @param field - Optional field name for the validation result
   * @returns An array containing a single validation result
   */
  isIntegerString: (v: unknown, field?: string): ValidationResult[] => {
    const valid = typeof v === 'string' && /^-?\d+$/.test(v)
    return [{ field: field || '', value: v, valid, message: valid ? undefined : `Invalid integer string: ${v}` }]
  },

  /**
   * Validates that a value is a string that represents a boolean ('true' or 'false').
   * @param v - The value to validate
   * @param field - Optional field name for the validation result
   * @returns An array containing a single validation result
   */
  isBooleanString: (v: unknown, field?: string): ValidationResult[] => {
    const valid = typeof v === 'string' && (v.toLowerCase() === 'true' || v.toLowerCase() === 'false')
    return [{ field: field || '', value: v, valid, message: valid ? undefined : `Invalid boolean string: ${v}` }]
  },

  /**
   * Validates that a value is an array.
   * @param v - The value to validate
   * @param field - Optional field name for the validation result
   * @returns An array containing a single validation result
   */
  isArray: (v: unknown, field?: string): ValidationResult[] => {
    const valid = Array.isArray(v)
    return [{ field: field || '', value: v, valid, message: valid ? undefined : `Expected array, got ${typeof v}` }]
  },

  /**
   * Validates that a value is one of the specified enum values.
   * @param e - The enum object
   * @returns A validator function that validates the value against the enum
   */
  isEnum<E extends Record<string, string | number>>(
    enumObj: E
  ): Validator {
    const entries = Object.entries(enumObj)
      .filter(([key]) =>
        isNaN(Number(key)) // skip reverse numeric keys
      )
  
    const enumValues = entries.map(([, val]) => val)
  
    const formatted = entries
      .map(([key, val]) => `${key}(${JSON.stringify(val)})`)
      .join(', ')
  
    return (value: unknown, field?: string): ValidationResult[] => {
      const isValid = (typeof value === 'string' || typeof value === 'number')
        && enumValues.includes(value)
  
      return [{
        field: field ?? '',
        value,
        valid: isValid,
        message: isValid ? undefined : `Expected one of: ${formatted}`
      }]
    }
  },

  /**
   * Validates that a value is a literal.
   * @param expected - The expected literal value
   * @returns A validator function that validates the value against the expected literal
   */
  literally: (expected: Literal): Validator => {
    return (value: unknown, field?: string): ValidationResult[] => {
      const valid = value === expected
  
      return [{
        field: field ?? '',
        value,
        valid,
        message: valid ? undefined : `Expected literal value ${JSON.stringify(expected)}`
      }]
    }
  },

  /**
   * Date and time related validators.
   */
  dt: {
    /**
     * Validates that a value is a valid ISO 8601 date string.
     * @param v - The value to validate
     * @param field - Optional field name for the validation result
     * @returns An array containing a single validation result
     */
    isISODateString: (v: unknown, field?: string): ValidationResult[] => {
      // Make sure the string is generally ISO date string format...
      const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?(Z|[+-]\d{2}:?\d{2}){1}$/

      if (typeof v !== 'string' || !isoRegex.test(v)) {
        return [{ field: field || '', value: v, valid: false, message: `Invalid ISO date string: ${v}` }]
      }

      // Then make sure it's a valid date/time...
      const isValid = !isNaN(new Date(v as string).valueOf())

      devWarn('[vkit] isISODateString validated as date string, but is not a Date object. If your expected type contains Date for fields, you will need to convert the string to a Date object.')

      return [{ field: field || '', value: v, valid: isValid, message: isValid ? undefined : `Invalid ISO date string: ${v}` }]
    }
  }
}